import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Button, Table, Tag, Statistic, Row, Col, Modal, Form,
  DatePicker, Input, message, Popconfirm, Alert,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getMyWorkDays, declareWorkDay, withdrawWorkDay, getMyInternPayments,
} from '../api/interns';
import useServerTable from '../hooks/useServerTable';
import { apiErrorMessage } from '../utils/apiError';
import { WORK_DAY_STATUS_TAG } from '../constants/internStatus';

const { TextArea } = Input;

const money = (v) =>
  v === null || v === undefined ? '—' : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`;

/**
 * Stajyerin kendi ekrani: "geldim" beyani, gecmis gunler ve bakiyesi.
 *
 * Bakiye stajyere GOSTERILIYOR - kendi alacagi. (Ogretmene gosterilmiyor,
 * o ayri bir izin; bkz. backend InternWorkDayController.)
 */
const InternMyDays = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [canDeclareFrom, setCanDeclareFrom] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const table = useServerTable('intern-my-days', 20);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getMyWorkDays(table.params());
      setRows(data.data);
      setTotal(data.total ?? 0);
      setSummary(data.summary);
      setCanDeclareFrom(data.can_declare_from);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Günler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [table.page, table.pageSize, table.sorter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPayments = useCallback(async () => {
    try {
      const { data } = await getMyInternPayments({ per_page: 10 });
      setPayments(data.data);
    } catch {
      // Ödeme listesi ikincil - sessizce geç, gün listesi yine çalışsın.
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleDeclare = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      await declareWorkDay({
        date: values.date.format('YYYY-MM-DD'),
        notes: values.notes,
      });
      message.success('Beyanınız kaydedildi, yönetici onayı bekleniyor.');
      setModalOpen(false);
      form.resetFields();
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Beyan kaydedilemedi.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async (id) => {
    try {
      await withdrawWorkDay(id);
      message.success('Beyan geri alındı.');
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Geri alınamadı.'));
    }
  };

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'date',
      key: 'date',
      render: (d) => dayjs(d).format('DD.MM.YYYY dddd'),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const t = WORK_DAY_STATUS_TAG[s] || {};
        return <Tag color={t.color}>{t.label || s}</Tag>;
      },
    },
    { title: 'Tutar', dataIndex: 'amount', key: 'amount', render: money },
    {
      title: 'Onaylayan',
      key: 'approved_by',
      render: (_, r) => (r.approver ? `${r.approver.name} ${r.approver.surname}` : '—'),
    },
    {
      title: 'Not',
      key: 'notes',
      render: (_, r) => r.reject_reason || r.notes || '—',
    },
    {
      title: 'İşlem',
      key: 'actions',
      render: (_, r) =>
        r.status === 'pending' ? (
          <Popconfirm title="Beyanı geri almak istiyor musunuz?" onConfirm={() => handleWithdraw(r.id)} okText="Geri Al" cancelText="Vazgeç">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ) : '—',
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Çalışma Günlerim</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          form.resetFields();
          form.setFieldsValue({ date: dayjs() });
          setModalOpen(true);
        }}>
          Geldiğimi Bildir
        </Button>
      </div>

      {summary && (
        <Row gutter={16} className="mb-4">
          <Col xs={12} md={6}><Card><Statistic title="Onaylı Gün" value={summary.approved_days} /></Card></Col>
          <Col xs={12} md={6}><Card><Statistic title="Onay Bekleyen" value={summary.pending_days} /></Card></Col>
          <Col xs={12} md={6}><Card><Statistic title="Toplam Hak Ediş" value={summary.earned} precision={2} suffix="₺" /></Card></Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Kalan Alacak"
                value={summary.balance}
                precision={2}
                suffix="₺"
                valueStyle={{ color: summary.balance > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card
        title="Günlerim"
        className="mb-4"
        extra={canDeclareFrom && (
          <span className="text-gray-500 text-sm">
            {dayjs(canDeclareFrom).format('DD.MM.YYYY')} tarihinden itibaren beyan verebilirsiniz
          </span>
        )}
      >
        <Table
          columns={columns.map((c) => ({ ...c, ...(['date', 'status', 'amount'].includes(c.key) ? table.sortProps(c.key) : {}) }))}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          size="small"
          {...table.tableProps(total)}
        />
      </Card>

      <Card title="Bana Yapılan Ödemeler">
        <Table
          dataSource={payments}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: 'Henüz ödeme kaydı yok.' }}
          columns={[
            { title: 'Tarih', dataIndex: 'paid_at', key: 'paid_at', render: (d) => dayjs(d).format('DD.MM.YYYY') },
            { title: 'Tutar', dataIndex: 'amount', key: 'amount', render: money },
            {
              title: 'Yöntem',
              dataIndex: 'payment_method',
              key: 'payment_method',
              render: (m) => (m === 'transfer' ? 'Havale/EFT' : 'Nakit'),
            },
            { title: 'Not', dataIndex: 'notes', key: 'notes', render: (n) => n || '—' },
          ]}
        />
      </Card>

      <Modal
        title="Geldiğimi Bildir"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleDeclare}
        confirmLoading={submitting}
        okText="Bildir"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Beyanınız yönetici onayından sonra hak edişinize eklenir."
        />
        <Form form={form} layout="vertical">
          <Form.Item name="date" label="Tarih" rules={[{ required: true, message: 'Tarih seçin.' }]}>
            <DatePicker
              format="DD.MM.YYYY"
              style={{ width: '100%' }}
              // İleri tarih ve izin verilen pencereden eski günler kapalı —
              // backend de aynı kuralı uyguluyor (tek başına UI yeterli değil).
              disabledDate={(d) =>
                d && (d.isAfter(dayjs(), 'day') || (canDeclareFrom && d.isBefore(dayjs(canDeclareFrom), 'day')))
              }
            />
          </Form.Item>
          <Form.Item name="notes" label="Not (isteğe bağlı)">
            <TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InternMyDays;
